import type { NodeKind, PageModel } from "@tento-ui/brand/page-model";

// Decorators are out-of-model UI (see escape-hatches.md): ephemeral promos,
// easter eggs, transient surfaces. A plugin declares where it goes (placement),
// whether it applies to a page, and a lazily-loaded body. The engine reserves a
// host (an in-flow slot, or the page root for overlays) and fills it.

export type FlowAnchor = { before: NodeKind } | { after: NodeKind };

export type Placement =
  | {
      kind: "flow";
      anchor: FlowAnchor;
      // Reserve height up front so a post-hydration fill does not shift the page.
      reserve: Option<{ minHeight: number }>;
    }
  | { kind: "overlay" };

export type DecoratorBody = {
  attach: ($host: HTMLElement, page: PageModel) => void | Promise<void>;
  detach: Option<($host: HTMLElement) => void>;
};

export type DecoratorPlugin = {
  id: string;
  placement: Placement;
  applies: (page: PageModel) => boolean;
  load: () => Promise<DecoratorBody>;
};

const registry = new Map<string, DecoratorPlugin>();
const loaded = new Map<string, DecoratorBody>();

export namespace PageDecorators {
  export function register(plugin: DecoratorPlugin): void {
    registry.set(plugin.id, plugin);
  }

  export function applicable(page: PageModel): DecoratorPlugin[] {
    const plugins: DecoratorPlugin[] = [];
    for (const plugin of registry.values()) {
      if (plugin.applies(page)) plugins.push(plugin);
    }
    return plugins;
  }

  // Cached so a re-render does not re-import an already-loaded body.
  export async function load(plugin: DecoratorPlugin): Promise<DecoratorBody> {
    const cached = loaded.get(plugin.id);
    if (cached) return cached;
    const body = await plugin.load();
    loaded.set(plugin.id, body);
    return body;
  }
}
